import { FormFields } from './types';
import { Search$, TSearch$State } from '../../../../effector/search';

type ValidationContext = {
  activeTabId: number;
  searchStore: TSearch$State;
  priceRange: { min: number; max: number } | null;
  limitOrderTimeoutMax: number;
};

type ValidationResult = true | string;

const onlyDigits = /^\d*\.?\d+$/;

const validateRequiredAndDigits = (value: string, required = true): ValidationResult => {
  const trimmed = value.trim();

  if (required && !trimmed) {
    return 'Поле является обязательным';
  }

  if (trimmed && !onlyDigits.test(trimmed)) {
    return 'Допустимы только цифры';
  }

  return true;
};

const validatePositiveNumber = (value: string, required = true): ValidationResult => {
  const trimmed = value.trim();

  const basicValidation = validateRequiredAndDigits(trimmed, required);

  if (!basicValidation) return basicValidation;

  const num = Number(trimmed);
  if (isNaN(num) || num <= 0) {
    return 'Значение должно быть больше 0';
  }

  return true;
};

const DELAYED_SIGNAL_TIMEOUT_MAX = 40;

const validateTimeout = (value: string, { activeTabId, limitOrderTimeoutMax }: ValidationContext): ValidationResult => {
  if (activeTabId !== 5 && activeTabId !== 2) {
    return true;
  }

  const isValidPositiveNumber = validatePositiveNumber(value, false);

  if (typeof isValidPositiveNumber === 'string') return validatePositiveNumber(value, false);

  const timeoutMax = activeTabId === 5 ? limitOrderTimeoutMax : DELAYED_SIGNAL_TIMEOUT_MAX;

  if (Number(value) > timeoutMax) return `Значение должно быть не больше ${timeoutMax}`;

  return true;
};

const validateNumericRange = (
  value: string,
  { type, min, max, fieldName }: { type: number; min?: number; max?: number; fieldName: 'SL' | 'TP' },
): ValidationResult => {
  const basicValidation = validateRequiredAndDigits(value, false);
  if (basicValidation !== true) return basicValidation;

  if (!value) return true;

  const numValue = Number(value);

  if (fieldName === 'SL') {
    if (type === 1 && max !== undefined && numValue > max) {
      return `SL не может быть выше чем ${max}`;
    }
    if (type === 2 && min !== undefined && numValue < min) {
      return `SL не может быть ниже чем ${min}`;
    }
  }

  if (fieldName === 'TP') {
    if (type === 1 && min !== undefined && numValue < min) {
      return `TP не может быть ниже чем ${min}`;
    }
    if (type === 2 && max !== undefined && numValue > max) {
      return `TP не может быть выше чем ${max}`;
    }
  }

  return true;
};

const validateWeight = (value: string, values: FormFields, { activeTabId, searchStore }: ValidationContext) => {
  const type = Number(values.type);

  // Для закрытия пропускаем проверку веса
  if (type === 3) {
    return true;
  }

  const basicValidation = validateRequiredAndDigits(value);
  if (basicValidation !== true) return basicValidation;

  const size = Number(value);

  const {
    maxSignalWeight,
    minWeight,
    maxWeight,
    currentWeight: rawCurrentWeight,
    shortAllowed,
  } = searchStore?.details || {};

  if (minWeight === undefined || maxWeight === undefined) {
    return 'Неверные данные для минимального или максимального веса';
  }

  const currentWeight = activeTabId === 4 ? 0 : Number(rawCurrentWeight);

  const newWeight = currentWeight + (type === 1 ? 1 : -1) * size;
  const resultWeightAbs = Math.abs(newWeight);
  const zeroWeight = newWeight === 0;

  if (activeTabId === 4 && maxSignalWeight && resultWeightAbs > maxSignalWeight) {
    return `Максимальный вес в сигнале равен ${maxSignalWeight}, уменьшите разницу весов`;
  }

  if (maxSignalWeight && size > maxSignalWeight) {
    return `Максимальный вес в сигнале равен ${maxSignalWeight}`;
  }

  if (!shortAllowed && newWeight < 0 && type !== 1) {
    return 'Отрицательный вес недопустим: для этого инструмента запрещены короткие позиции';
  }

  const inRange = resultWeightAbs >= minWeight && resultWeightAbs <= maxWeight;
  const acceptable = shortAllowed
    ? zeroWeight || inRange
    : zeroWeight || (newWeight >= 0 && inRange) || (newWeight < 0 && newWeight > currentWeight);

  if (!acceptable) {
    const positiveRange = `${minWeight - currentWeight} до ${maxWeight - currentWeight}`;
    return `По данной позиции доступно либо закрытие, либо вес в диапазоне от ${positiveRange}`;
  }

  return true;
};

const validateStopLoss = (value: string, values: FormFields, { searchStore }: ValidationContext): ValidationResult => {
  const details = searchStore?.details;
  return validateNumericRange(value, {
    type: Number(values.type),
    min: details?.minSLSell,
    max: details?.maxSLBuy,
    fieldName: 'SL',
  });
};

const validateTakeProfit = (
  value: string,
  values: FormFields,
  { searchStore }: ValidationContext,
): ValidationResult => {
  const details = searchStore?.details;
  return validateNumericRange(value, {
    type: Number(values.type),
    min: details?.minTPBuy,
    max: details?.maxTPSell,
    fieldName: 'TP',
  });
};

const validateType = (value: string): ValidationResult => {
  return value ? true : 'Поле является обязательным';
};

const validatePrice = (
  value: string,
  values: FormFields,
  { activeTabId, priceRange }: ValidationContext,
): ValidationResult => {
  if (activeTabId !== 5) {
    return true;
  }

  const basicValidation = validateRequiredAndDigits(value);
  if (basicValidation !== true) return basicValidation;

  if (!priceRange) return true;

  const price = Number(value);

  const { min, max } = priceRange;

  if (price < min || price > max) {
    return `Цена должна находиться в пределах стакана (${min} - ${max})`;
  }

  return true;
};

export const createValidationSchema = (
  activeTabId: number,
  priceRange: { min: number; max: number } | null,
  limitOrderTimeoutMax: number,
) => {
  const searchStore = Search$.getState();
  const context: ValidationContext = { activeTabId, searchStore, priceRange, limitOrderTimeoutMax };

  return {
    size: (value: string, values: FormFields) => validateWeight(value, values, context),
    stoploss: (value: string, values: FormFields) => validateStopLoss(value, values, context),
    takeprofit: (value: string, values: FormFields) => validateTakeProfit(value, values, context),
    type: (value: string) => validateType(value),
    price: (value: string, values: FormFields) => validatePrice(value, values, context),
    timeout: (value: string) => validateTimeout(value, context),
  };
};
